import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../shared/components/PageHeader';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { ItemDetail, Tag } from '../../core/types/data-models';

/**
 * Per-item detail page. Demonstrates two relation patterns side by side:
 *
 * - **1-to-1** (Item ↔ ItemDetail) — fetched via `?filter[itemId]=...`,
 *   upserted by creating-or-updating depending on whether a row exists.
 * - **M:N** (Item ↔ Tag) — attach/detach against the junction endpoint
 *   at `/items/:itemId/tags/:tagId`.
 */
export function ItemDetailPage() {
  const params = useParams<{ id: string }>();
  const itemId = Number(params.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const itemQuery = useQuery({
    queryKey: ['items', itemId],
    queryFn: () => resources.items.get(itemId),
    enabled: Number.isFinite(itemId),
  });

  // 1-to-1: fetch the detail row via filter.
  const detailQuery = useQuery({
    queryKey: ['item-details', { itemId }],
    queryFn: () =>
      resources.itemDetails.list({
        pageSize: 1,
        filter: { itemId },
      }),
    enabled: Number.isFinite(itemId),
  });

  const existingDetail = detailQuery.data?.data[0] ?? null;

  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotes(existingDetail?.notes ?? '');
    setPriority(existingDetail?.priority ?? 0);
  }, [existingDetail]);

  const saveDetail = useMutation({
    mutationFn: async () => {
      const payload: Partial<ItemDetail> = { itemId, notes, priority };
      return existingDetail
        ? resources.itemDetails.update(existingDetail.id, payload)
        : resources.itemDetails.create(payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['item-details'] }),
  });

  // M:N: list current tags + the global tag catalog for an Autocomplete picker.
  const tagsService = resources.itemTagsFor(itemId);
  const attachedQuery = useQuery({
    queryKey: ['items', itemId, 'tags'],
    queryFn: () => tagsService.list({ pageSize: 100, include: ['tag'] }),
    enabled: Number.isFinite(itemId),
  });

  const catalogQuery = useQuery({
    queryKey: ['tags', 'select'],
    queryFn: () => resources.tags.list({ pageSize: 200, sort: 'name' }),
  });

  const invalidateTags = () => queryClient.invalidateQueries({ queryKey: ['items', itemId, 'tags'] });

  const attachTag = useMutation({
    mutationFn: (tagId: number) => tagsService.attach(tagId),
    onSuccess: invalidateTags,
  });
  const detachTag = useMutation({
    mutationFn: (tagId: number) => tagsService.detach(tagId),
    onSuccess: invalidateTags,
  });

  const attached = attachedQuery.data?.data ?? [];
  const attachedTagIds = new Set(attached.map((a) => a.tagId));
  const candidates = (catalogQuery.data?.data ?? []).filter((t) => !attachedTagIds.has(t.id));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={itemQuery.data?.name ?? `Item #${itemId}`}
        description={itemQuery.data?.description ?? undefined}
        actions={
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/items')}>
            Back
          </Button>
        }
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* 1-to-1 panel */}
        <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" gutterBottom>
            Detail (1-to-1)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            One row per item — backed by a UNIQUE constraint on{' '}
            <code>itemId</code>.
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Notes"
              fullWidth
              multiline
              minRows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <TextField
              label="Priority"
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value) || 0)}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Box>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saveDetail.isPending}
                onClick={async () => {
                  setError(null);
                  try {
                    await saveDetail.mutateAsync();
                  } catch (err) {
                    setError(extractErrorMessage(err));
                  }
                }}
              >
                {existingDetail ? 'Save' : 'Create detail'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        {/* M:N panel */}
        <Paper sx={{ p: 2, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle1" gutterBottom>
            Tags (M:N)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Attach tags from the global catalog. PUT is idempotent at{' '}
            <code>/items/{itemId}/tags/:tagId</code>.
          </Typography>

          <Autocomplete<Tag, false, false, false>
            sx={{ mt: 2 }}
            options={candidates}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, next) => {
              if (next) attachTag.mutate(next.id);
            }}
            value={null}
            blurOnSelect
            renderInput={(p) => <TextField {...p} label="Attach a tag" />}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
            {attached.map((row) => (
              <Chip
                key={row.id}
                label={row.tag?.name ?? `#${row.tagId}`}
                onDelete={() => detachTag.mutate(row.tagId)}
                deleteIcon={<DeleteIcon />}
                sx={{
                  backgroundColor: row.tag?.color ?? undefined,
                  color: row.tag?.color ? '#fff' : undefined,
                }}
              />
            ))}
            {attached.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No tags attached yet.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}
